/**
 * ReferenceLibraryView Component - React Flow canvas for reference image nodes
 *
 * A canvas for organizing reference images, mood boards, character refs, and location photos.
 * Similar to NodeView but simplified (no timeline, no shelf, no comment boxes).
 *
 * Selection Strategy:
 * - selectedReferenceIds accessed in flowNodes memo but NOT in dependencies
 * - This prevents infinite loops from selection changes → memo recompute → new objects → StoreUpdater → loop
 * - Selection changes handled via onSelectionChange callback
 * - 'select' type changes filtered out in onNodesChange to prevent circular updates
 */

import { useCallback, useMemo, useRef, useEffect, CSSProperties, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  ControlButton,
  Node,
  NodeChange,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  NodeTypes,
  SelectionMode,
  type Viewport,
  applyNodeChanges,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import ReferenceNode from './ReferenceNode';
import NoteNode from './NoteNode';
import ContextMenu, { ContextMenuItem } from '@/components/ContextMenu/ContextMenu';
import { useProjectStore } from '@/stores/projectStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useViewStore } from '@/stores/viewStore';
import { ReferenceNodeData, NoteNodeData } from '@/types';
import { resizeImageForImport } from '@/utils/imageProcessing';
import { NODE_MAX_COUNT } from '@/constants/app';
import { blobManager } from '@/utils/blobManager';

function ReferenceLibraryViewInner() {
  const referenceNodes = useProjectStore((state) => state.referenceNodes);
  const addReferenceNode = useProjectStore((state) => state.addReferenceNode);
  const updateReferenceNode = useProjectStore((state) => state.updateReferenceNode);
  const updateReferencePosition = useProjectStore((state) => state.updateReferencePosition);
  const deleteReferenceNode = useProjectStore((state) => state.deleteReferenceNode);
  const duplicateReferenceNode = useProjectStore((state) => state.duplicateReferenceNode);
  const selectReferenceNode = useProjectStore((state) => state.selectReferenceNode);
  const selectReferenceNodes = useProjectStore((state) => state.selectReferenceNodes);
  const clearReferenceSelection = useProjectStore((state) => state.clearReferenceSelection);
  const selectedReferenceIds = useProjectStore((state) => state.selectedReferenceIds);

  const noteNodes = useProjectStore((state) => state.noteNodes);
  const addNoteNode = useProjectStore((state) => state.addNoteNode);
  const updateNotePosition = useProjectStore((state) => state.updateNotePosition);
  const updateNoteSize = useProjectStore((state) => state.updateNoteSize);
  const deleteNoteNode = useProjectStore((state) => state.deleteNoteNode);
  const duplicateNoteNode = useProjectStore((state) => state.duplicateNoteNode);
  const selectNoteNode = useProjectStore((state) => state.selectNoteNode);
  const selectNoteNodes = useProjectStore((state) => state.selectNoteNodes);
  const clearNoteSelection = useProjectStore((state) => state.clearNoteSelection);
  const selectedNoteIds = useProjectStore((state) => state.selectedNoteIds);

  const saveHistory = useProjectStore((state) => state.saveHistory);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const referencesViewport = useViewStore((state) => state.referencesViewport);
  const setReferencesViewport = useViewStore((state) => state.setReferencesViewport);

  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition, fitView, getViewport } = reactFlowInstance;

  // Track pending dimension updates with throttling
  const dimensionUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track last clicked position for paste
  const lastClickedPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Track Alt+drag duplication state
  const isDuplicatingRef = useRef<string | null>(null);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);

  // Track drag state for visual feedback
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);

  // Context menu state for right-click paste
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [clipboardType, setClipboardType] = useState<'image' | 'text' | 'none'>('none');

  // Register our custom node types
  const nodeTypes: NodeTypes = useMemo(() => ({
    reference: ReferenceNode,
    note: NoteNode,
  }), []);

  // Map our stored nodes to React Flow format with selected state and dimensions
  // NOTE: selectedReferenceIds/selectedNoteIds IS included to sync external selection changes
  // Infinite loop prevented by: onNodesChange filters 'select' AND onSelectionChange uses ref check
  const flowNodes = useMemo(
    () => {
      const refNodes = referenceNodes.map((node) => {
        const width = node.data.userWidth || node.data.nodeWidth || 220;
        const height = node.data.userHeight || node.data.nodeHeight || 160;

        // Calculate aspect ratio from original image dimensions
        const imageWidth = node.data.imageWidth || 220;
        const imageHeight = node.data.imageHeight || 160;
        const aspectRatio = imageWidth / imageHeight;

        return {
          ...node,
          selected: selectedReferenceIds.includes(node.id),
          // Use measured property for React Flow v12 resize support
          measured: {
            width,
            height,
          },
          // Tell React Flow to maintain aspect ratio natively
          resizing: false,
          style: {
            width,
            height,
          },
        } as Node & { resizing?: boolean };
      });

      const notesAsFlowNodes = noteNodes.map((node) => {
        const width = node.width || 300;
        const height = node.height || 200;
        return {
          ...node,
          selected: selectedNoteIds.includes(node.id),
          // Use measured property for React Flow v12 resize support
          measured: {
            width,
            height,
          },
          style: {
            width,
            height,
          },
        } as Node;
      });

      return [...refNodes, ...notesAsFlowNodes];
    },
    [referenceNodes, selectedReferenceIds, noteNodes, selectedNoteIds]
  );

  // Track previous selection to prevent infinite loops
  const prevSelectionRef = useRef<string[]>([]);

  // Update ref when selectedReferenceIds changes (but after onSelectionChange check)
  useEffect(() => {
    prevSelectionRef.current = selectedReferenceIds;
  }, [selectedReferenceIds]);

  // ---------------------------------------------------------------------------
  // NODE POSITION UPDATES
  // ---------------------------------------------------------------------------

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Filter out 'select' changes - they're handled by onSelectionChange
      const filteredChanges = changes.filter(change => change.type !== 'select');

      if (filteredChanges.length === 0) return;

      // Check if there are any remove changes - save history before
      // (dimension changes are handled by resize handlers which save history on start)
      const hasRemove = filteredChanges.some((change) => change.type === 'remove');

      if (hasRemove) {
        saveHistory();
      }

      filteredChanges.forEach((change) => {
        // Determine node type by checking which array contains this ID
        const isReferenceNode = referenceNodes.some((node) => node.id === change.id);
        const isNoteNode = noteNodes.some((node) => node.id === change.id);

        if (change.type === 'position' && change.position) {
          // If we're duplicating, update the duplicate's position instead of the original
          if (isDuplicatingRef.current) {
            if (isReferenceNode) {
              updateReferencePosition(isDuplicatingRef.current, change.position);
            } else if (isNoteNode) {
              updateNotePosition(isDuplicatingRef.current, change.position);
            }
          } else {
            // Normal position update
            if (isReferenceNode) {
              updateReferencePosition(change.id, change.position);
            } else if (isNoteNode) {
              updateNotePosition(change.id, change.position);
            }
          }
        } else if (change.type === 'dimensions' && change.dimensions) {
          // Handle resize for note nodes
          // Reference nodes handle aspect-locked dimensions in handleResizeEnd only
          if (isNoteNode) {
            updateNoteSize(change.id, change.dimensions.width, change.dimensions.height);
          }
          // Skip dimension updates for reference nodes - they're handled in handleResizeEnd
        } else if (change.type === 'remove') {
          if (isReferenceNode) {
            deleteReferenceNode(change.id);
          } else if (isNoteNode) {
            deleteNoteNode(change.id);
          }
        }
      });

      // Show notification if nodes were deleted
      if (hasRemove) {
        const removeCount = filteredChanges.filter((change) => change.type === 'remove').length;
        showNotification(
          `Deleted ${removeCount} node${removeCount > 1 ? 's' : ''}`,
          'info',
          2000
        );
      }
    },
    [
      updateReferencePosition,
      updateReferenceNode,
      deleteReferenceNode,
      referenceNodes,
      noteNodes,
      updateNotePosition,
      updateNoteSize,
      deleteNoteNode,
      saveHistory,
      showNotification,
    ]
  );

  // ---------------------------------------------------------------------------
  // SELECTION HANDLING
  // ---------------------------------------------------------------------------

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const isReferenceNode = node.type === 'reference';
      const isNoteNode = node.type === 'note';

      if (event.shiftKey) {
        // Multi-select with Shift - handle each type separately
        if (isReferenceNode) {
          const newSelection = selectedReferenceIds.includes(node.id)
            ? selectedReferenceIds.filter((id) => id !== node.id)
            : [...selectedReferenceIds, node.id];
          selectReferenceNodes(newSelection);
        } else if (isNoteNode) {
          const newSelection = selectedNoteIds.includes(node.id)
            ? selectedNoteIds.filter((id) => id !== node.id)
            : [...selectedNoteIds, node.id];
          selectNoteNodes(newSelection);
        }
      } else {
        // Single select - clear other type's selection
        if (isReferenceNode) {
          selectReferenceNode(node.id);
          clearNoteSelection();
        } else if (isNoteNode) {
          selectNoteNode(node.id);
          clearReferenceSelection();
        }
      }
    },
    [
      selectReferenceNode,
      selectReferenceNodes,
      selectedReferenceIds,
      selectNoteNode,
      selectNoteNodes,
      selectedNoteIds,
      clearNoteSelection,
      clearReferenceSelection,
    ]
  );

  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      // Deselect all if clicking on empty space
      if (!event.shiftKey) {
        selectReferenceNode(null);
        selectNoteNode(null);
      }

      // Store position for paste
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      lastClickedPositionRef.current = position;
    },
    [selectReferenceNode, selectNoteNode, screenToFlowPosition]
  );

  const onSelectionChange = useCallback(
    (params: { nodes: Node[] }) => {
      const newSelection = params.nodes.map((node) => node.id).sort();
      const prevSelection = [...prevSelectionRef.current].sort();

      // Only update if selection actually changed (prevent infinite loop)
      if (JSON.stringify(newSelection) !== JSON.stringify(prevSelection)) {
        prevSelectionRef.current = params.nodes.map((node) => node.id);

        // Separate reference and note nodes
        const refIds = params.nodes
          .filter((node) => node.type === 'reference')
          .map((node) => node.id);
        const noteIds = params.nodes
          .filter((node) => node.type === 'note')
          .map((node) => node.id);

        selectReferenceNodes(refIds);
        selectNoteNodes(noteIds);
      }
    },
    [selectReferenceNodes, selectNoteNodes]
  );

  // ---------------------------------------------------------------------------
  // ALT+DRAG DUPLICATION
  // ---------------------------------------------------------------------------

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Save history before drag starts
      saveHistory();

      const isReferenceNode = node.type === 'reference';
      const isNoteNode = node.type === 'note';

      // If Alt key is pressed, prepare to duplicate
      if (event.altKey) {
        dragStartPosRef.current = node.position;
        isDuplicatingRef.current = node.id;
      }
    },
    [saveHistory]
  );

  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // If we're set to duplicate but haven't created the duplicate yet
      if (isDuplicatingRef.current === node.id && dragStartPosRef.current) {
        // Check if we've moved enough to trigger duplication (at least 5 pixels)
        const dx = Math.abs(node.position.x - dragStartPosRef.current.x);
        const dy = Math.abs(node.position.y - dragStartPosRef.current.y);

        if (dx > 5 || dy > 5) {
          const isReferenceNode = node.type === 'reference';
          const isNoteNode = node.type === 'note';

          if (isReferenceNode) {
            // Create duplicate at the original starting position
            const newNode = duplicateReferenceNode(node.id, dragStartPosRef.current);
            if (newNode) {
              // Update isDuplicatingRef to the new node's ID
              // Now onNodesChange will redirect position updates to this duplicate
              isDuplicatingRef.current = newNode.id;
              selectReferenceNode(newNode.id);
              clearNoteSelection();
            }
          } else if (isNoteNode) {
            // Create duplicate at the original starting position
            const newNode = duplicateNoteNode(node.id, dragStartPosRef.current);
            if (newNode) {
              // Update isDuplicatingRef to the new node's ID
              // Now onNodesChange will redirect position updates to this duplicate
              isDuplicatingRef.current = newNode.id;
              selectNoteNode(newNode.id);
              clearReferenceSelection();
            }
          }
        }
      }
    },
    [
      duplicateReferenceNode,
      duplicateNoteNode,
      selectReferenceNode,
      selectNoteNode,
      clearNoteSelection,
      clearReferenceSelection,
    ]
  );

  const onNodeDragStop = useCallback(() => {
    // Clear duplication state
    isDuplicatingRef.current = null;
    dragStartPosRef.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // DOUBLE-CLICK TO CREATE REFERENCE
  // ---------------------------------------------------------------------------

  const onDoubleClickHandler = useCallback(
    (event: React.MouseEvent) => {
      // Don't create reference node if double-clicking on an existing node
      // Check if the click target is inside a node (not the pane background)
      const target = event.target as HTMLElement;
      if (target.closest('.react-flow__node')) {
        return; // Clicked on a node, don't create new reference
      }

      // Check if we've hit the max node count
      if (referenceNodes.length >= NODE_MAX_COUNT) {
        showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
        return;
      }

      const clickPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });

      // Default reference node dimensions (used for centering)
      const DEFAULT_NODE_WIDTH = 220;
      const DEFAULT_NODE_HEIGHT = 160;

      // Center the node on the click position
      const centeredPosition = {
        x: clickPosition.x - DEFAULT_NODE_WIDTH / 2,
        y: clickPosition.y - DEFAULT_NODE_HEIGHT / 2,
      };

      saveHistory();
      const newNode = addReferenceNode(centeredPosition);
      selectReferenceNode(newNode.id);
      showNotification('New reference created', 'info', 1500);
    },
    [referenceNodes.length, screenToFlowPosition, saveHistory, addReferenceNode, selectReferenceNode, showNotification]
  );

  // ---------------------------------------------------------------------------
  // DRAG & DROP IMAGE IMPORT
  // ---------------------------------------------------------------------------

  const handlePaneDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current++;
    
    // Check if we're dragging files or URLs
    const hasFiles = event.dataTransfer.types.includes('Files');
    const hasHtml = event.dataTransfer.types.includes('text/html');
    const hasUri = event.dataTransfer.types.includes('text/uri-list');
    const hasText = event.dataTransfer.types.includes('text/plain');
    
    if (hasFiles || hasHtml || hasUri || hasText) {
      setIsDraggingOver(true);
    }
  }, []);

  const handlePaneDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current--;
    
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  }, []);

  const handlePaneDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handlePaneDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Reset drag state
      dragCounterRef.current = 0;
      setIsDraggingOver(false);

      // Handle files dragged from file system
      const files = Array.from(event.dataTransfer.files);
      const imageFiles = files.filter((file) => file.type.startsWith('image/'));

      // Handle web images (dragged from browser)
      const htmlData = event.dataTransfer.getData('text/html');
      let webImageUrl: string | null = null;

      if (htmlData) {
        // Parse HTML to find image src
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlData, 'text/html');
        const img = doc.querySelector('img');
        if (img) {
          webImageUrl = img.src;
        }
      }

      // Also check for direct URL from uri-list
      if (!webImageUrl) {
        const urlData = event.dataTransfer.getData('text/uri-list');
        if (urlData && urlData.match(/\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i)) {
          webImageUrl = urlData.split('\n')[0].trim();
        }
      }

      // Check for plain text URL (some browsers/websites send it this way)
      if (!webImageUrl) {
        const textData = event.dataTransfer.getData('text/plain');
        if (textData && textData.match(/^https?:\/\/.+\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i)) {
          webImageUrl = textData.trim();
        }
      }

      // Process file drops
      if (imageFiles.length > 0) {
        // Check if we would exceed the max node count
        if (referenceNodes.length + imageFiles.length > NODE_MAX_COUNT) {
          showNotification(
            `Cannot import ${imageFiles.length} images. Maximum ${NODE_MAX_COUNT} nodes allowed.`,
            'error',
            3000
          );
          return;
        }

        saveHistory();

        const dropPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        const GRID_SPACING = 350; // Horizontal spacing between dropped images
        const GRID_COLUMNS = 3; // Number of images per row
        const DEFAULT_NODE_WIDTH = 220;
        const DEFAULT_NODE_HEIGHT = 160;

        // Center the first node on the drop position, then arrange rest in grid
        const firstNodePosition = {
          x: dropPosition.x - DEFAULT_NODE_WIDTH / 2,
          y: dropPosition.y - DEFAULT_NODE_HEIGHT / 2,
        };

        // Create reference nodes in a grid layout
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];

          try {
            // Resize image if needed (same as beat images - 2MP limit)
            const resizedBlob = await resizeImageForImport(file);

            // Calculate grid position (relative to first node)
            const col = i % GRID_COLUMNS;
            const row = Math.floor(i / GRID_COLUMNS);
            const position = {
              x: firstNodePosition.x + (col * GRID_SPACING),
              y: firstNodePosition.y + (row * GRID_SPACING),
            };

            // Create reference node without image first
            const newNode = addReferenceNode(position, {
              title: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
              imagePath: null,
            });

            // Now create blob with the actual node ID and update the node
            const finalKey = `reference-image:${newNode.id}`;
            blobManager.create(resizedBlob, finalKey);
            // Store the key (not the blob URL) so it persists across saves/loads
            updateReferenceNode(newNode.id, { imagePath: finalKey });
          } catch (error) {
            console.error('Failed to process image:', error);
            showNotification(`Failed to import ${file.name}`, 'error', 3000);
          }
        }

        showNotification(
          `Imported ${imageFiles.length} reference${imageFiles.length > 1 ? 's' : ''}`,
          'info',
          2000
        );
        return;
      }

      // Process web image drops
      if (webImageUrl) {
        // Check node count
        if (referenceNodes.length >= NODE_MAX_COUNT) {
          showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
          return;
        }

        saveHistory();

        try {
          // Fetch the image
          const response = await fetch(webImageUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
          }

          const blob = await response.blob();
          if (!blob.type.startsWith('image/')) {
            throw new Error('Dropped item is not an image');
          }

          // Create a file from blob
          const fileName = webImageUrl.split('/').pop()?.split('?')[0] || 'web-image.png';
          const file = new File([blob], fileName, { type: blob.type });

          // Resize image
          const resizedBlob = await resizeImageForImport(file, 1280, 1280);

          const dropPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
          const DEFAULT_NODE_WIDTH = 220;
          const DEFAULT_NODE_HEIGHT = 160;

          const position = {
            x: dropPosition.x - DEFAULT_NODE_WIDTH / 2,
            y: dropPosition.y - DEFAULT_NODE_HEIGHT / 2,
          };

          // Create reference node
          const newNode = addReferenceNode(position, {
            title: fileName.replace(/\.[^/.]+$/, ''),
            imagePath: null,
          });

          // Store image
          const finalKey = `reference-image:${newNode.id}`;
          blobManager.create(resizedBlob, finalKey);
          updateReferenceNode(newNode.id, { imagePath: finalKey });

          selectReferenceNode(newNode.id);
          clearNoteSelection();

          showNotification('Reference image imported from web', 'info', 2000);
        } catch (error) {
          console.error('Failed to import web image:', error);
          showNotification('Failed to import web image. Try downloading it first.', 'error', 3000);
        }
        return;
      }
    },
    [referenceNodes.length, noteNodes.length, screenToFlowPosition, saveHistory, addReferenceNode, updateReferenceNode, selectReferenceNode, clearNoteSelection, showNotification]
  );

  // ---------------------------------------------------------------------------
  // CONTEXT MENU (RIGHT-CLICK)
  // ---------------------------------------------------------------------------

  // Check clipboard content type
  const checkClipboard = useCallback(async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        setClipboardType('none');
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        // Check for images first
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            setClipboardType('image');
            return;
          }
        }
        // Check for text
        for (const type of item.types) {
          if (type === 'text/plain') {
            setClipboardType('text');
            return;
          }
        }
      }
      setClipboardType('none');
    } catch (e) {
      // Clipboard API might be blocked
      setClipboardType('none');
    }
  }, []);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    // Don't show context menu when right-clicking on nodes
    const target = event.target as HTMLElement;
    if (target.closest('.react-flow__node')) {
      return;
    }

    event.preventDefault();
    
    // Check clipboard content
    checkClipboard();
    
    // Store click position for paste
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    lastClickedPositionRef.current = position;
    
    // Position menu offset from cursor to avoid browser permission popup
    // The browser's clipboard permission prompt typically appears at the cursor position
    // We shift the menu down-right significantly to avoid overlap
    const offsetX = event.clientX + 40;
    const offsetY = event.clientY + 40;
    setContextMenu({ x: offsetX, y: offsetY });
  }, [screenToFlowPosition, checkClipboard]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
    setClipboardType('none');
  }, []);

  const handlePasteImageFromContextMenu = useCallback(async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        showNotification('Clipboard API not available. Use Ctrl+V to paste.', 'error', 3000);
        handleCloseContextMenu();
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            
            const totalNodes = referenceNodes.length + noteNodes.length;
            if (totalNodes >= NODE_MAX_COUNT) {
              showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
              handleCloseContextMenu();
              return;
            }

            saveHistory();
            const DEFAULT_NODE_WIDTH = 220;
            const DEFAULT_NODE_HEIGHT = 160;
            const pasteX = lastClickedPositionRef.current.x;
            const pasteY = lastClickedPositionRef.current.y;
            const file = new File([blob], `clipboard-${Date.now()}.png`, { type });
            const resizedBlob = await resizeImageForImport(file, 1280, 1280);
            const position = {
              x: pasteX - DEFAULT_NODE_WIDTH / 2,
              y: pasteY - DEFAULT_NODE_HEIGHT / 2,
            };
            const newNode = addReferenceNode(position, {
              title: file.name.replace(/\.[^/.]+$/, ''),
              imagePath: null,
            });
            const finalKey = `reference-image:${newNode.id}`;
            blobManager.create(resizedBlob, finalKey);
            updateReferenceNode(newNode.id, { imagePath: finalKey });
            selectReferenceNode(newNode.id);
            clearNoteSelection();
            showNotification('Reference image pasted', 'info', 1500);
            handleCloseContextMenu();
            return;
          }
        }
      }
      showNotification('No image found in clipboard', 'error', 2000);
    } catch (error) {
      console.error('Failed to paste image:', error);
      showNotification('Failed to paste image. Try using Ctrl+V.', 'error', 3000);
    }
    handleCloseContextMenu();
  }, [referenceNodes.length, noteNodes.length, addReferenceNode, updateReferenceNode, selectReferenceNode, clearNoteSelection, saveHistory, showNotification, handleCloseContextMenu]);

  const handlePasteTextAsNote = useCallback(async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        showNotification('Clipboard API not available.', 'error', 3000);
        handleCloseContextMenu();
        return;
      }

      const text = await navigator.clipboard.readText();
      
      if (!text.trim()) {
        showNotification('No text found in clipboard', 'error', 2000);
        handleCloseContextMenu();
        return;
      }

      const totalNodes = referenceNodes.length + noteNodes.length;
      if (totalNodes >= NODE_MAX_COUNT) {
        showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
        handleCloseContextMenu();
        return;
      }

      saveHistory();
      const DEFAULT_NOTE_WIDTH = 300;
      const DEFAULT_NOTE_HEIGHT = 200;
      const pasteX = lastClickedPositionRef.current.x;
      const pasteY = lastClickedPositionRef.current.y;
      
      const position = {
        x: pasteX - DEFAULT_NOTE_WIDTH / 2,
        y: pasteY - DEFAULT_NOTE_HEIGHT / 2,
      };
      
      const newNote = addNoteNode(position, {
        title: 'Pasted Note',
        content: text,
      });
      selectNoteNode(newNote.id);
      clearReferenceSelection();
      showNotification('Note created from clipboard', 'info', 1500);
    } catch (error) {
      console.error('Failed to paste text:', error);
      showNotification('Failed to paste text.', 'error', 3000);
    }
    handleCloseContextMenu();
  }, [referenceNodes.length, noteNodes.length, addNoteNode, selectNoteNode, clearReferenceSelection, saveHistory, showNotification, handleCloseContextMenu]);

  // Build context menu items dynamically based on clipboard content
  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Create Note',
      icon: '📝',
      onClick: () => {
        handleCreateNote();
        handleCloseContextMenu();
      },
    },
  ];

  // Add paste options based on clipboard content
  if (clipboardType === 'image') {
    contextMenuItems.push({
      label: 'Paste Image',
      icon: '📋',
      onClick: handlePasteImageFromContextMenu,
    });
  } else if (clipboardType === 'text') {
    contextMenuItems.push({
      label: 'Paste and Create Note',
      icon: '📋',
      onClick: handlePasteTextAsNote,
    });
  } else {
    // If clipboard type is unknown/none, show both paste options
    // They'll be disabled or show error if nothing to paste
    contextMenuItems.push({
      label: 'Paste Image',
      icon: '📋',
      onClick: handlePasteImageFromContextMenu,
    });
  }

  // ---------------------------------------------------------------------------
  // CLIPBOARD PASTE HANDLER (KEYBOARD)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handlePaste = async (event: ClipboardEvent) => {
      // Don't trigger when pasting in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const items = event.clipboardData?.items;
      if (!items) return;

      // Check for images in clipboard
      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          hasImage = true;
          break;
        }
      }

      if (!hasImage) return;

      // Prevent default to stop any other paste behavior
      event.preventDefault();

      // Check node count limit
      const totalNodes = referenceNodes.length + noteNodes.length;
      if (totalNodes >= NODE_MAX_COUNT) {
        showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
        return;
      }

      saveHistory();

      const DEFAULT_NODE_WIDTH = 220;
      const DEFAULT_NODE_HEIGHT = 160;

      // Get paste position (use last clicked position or center)
      let pasteX = lastClickedPositionRef.current.x;
      let pasteY = lastClickedPositionRef.current.y;

      // If no click position, use viewport center
      if (pasteX === 0 && pasteY === 0) {
        const viewport = getViewport();
        if (viewport) {
          pasteX = (-viewport.x + window.innerWidth / 2) / viewport.zoom;
          pasteY = (-viewport.y + window.innerHeight / 2) / viewport.zoom;
        }
      }

      // Process each image in clipboard
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (!blob) continue;

          try {
            // Convert blob to File for resizing
            const file = new File([blob], `clipboard-${Date.now()}.png`, { type: blob.type });

            // Resize image for reference (1280px max width)
            const resizedBlob = await resizeImageForImport(file, 1280, 1280);

            // Calculate position (center on paste position)
            const position = {
              x: pasteX - DEFAULT_NODE_WIDTH / 2,
              y: pasteY - DEFAULT_NODE_HEIGHT / 2,
            };

            // Create reference node
            const newNode = addReferenceNode(position, {
              title: file.name.replace(/\.[^/.]+$/, ''),
              imagePath: null,
            });

            // Create blob with node ID and update
            const finalKey = `reference-image:${newNode.id}`;
            blobManager.create(resizedBlob, finalKey);
            updateReferenceNode(newNode.id, { imagePath: finalKey });

            // Select the new node
            selectReferenceNode(newNode.id);
            clearNoteSelection();

            showNotification('Reference image pasted', 'info', 1500);
          } catch (error) {
            console.error('Failed to paste image:', error);
            showNotification('Failed to paste image', 'error', 2000);
          }
          break; // Only paste first image
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [referenceNodes.length, noteNodes.length, addReferenceNode, updateReferenceNode, selectReferenceNode, clearNoteSelection, saveHistory, showNotification, getViewport]);

  // ---------------------------------------------------------------------------
  // KEYBOARD SHORTCUTS
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if target is an input element - don't trigger shortcuts while typing
      if (
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLInputElement
      ) {
        return;
      }

      // N key - Create note node at cursor position
      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();

        // Check max node count (notes + references combined)
        if (referenceNodes.length + noteNodes.length >= NODE_MAX_COUNT) {
          showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
          return;
        }

        saveHistory();

        // Default note dimensions
        const DEFAULT_NOTE_WIDTH = 300;
        const DEFAULT_NOTE_HEIGHT = 200;

        // Use last clicked position (or viewport center as fallback)
        let targetX = lastClickedPositionRef.current.x;
        let targetY = lastClickedPositionRef.current.y;

        // If no click position recorded (e.g., first use), use viewport center
        if (targetX === 0 && targetY === 0) {
          const viewport = getViewport();
          if (!viewport) return;
          targetX = (-viewport.x + window.innerWidth / 2) / viewport.zoom;
          targetY = (-viewport.y + window.innerHeight / 2) / viewport.zoom;
        }

        // Center the note on the target position
        const centeredPosition = {
          x: targetX - DEFAULT_NOTE_WIDTH / 2,
          y: targetY - DEFAULT_NOTE_HEIGHT / 2,
        };

        const newNote = addNoteNode(centeredPosition);
        selectNoteNode(newNote.id);
        showNotification('New note created', 'info', 1500);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    referenceNodes.length,
    noteNodes.length,
    saveHistory,
    addNoteNode,
    selectNoteNode,
    showNotification,
    getViewport,
  ]);

  // ---------------------------------------------------------------------------
  // VIEWPORT PERSISTENCE
  // ---------------------------------------------------------------------------

  const onViewportChange = useCallback(
    (viewport: Viewport) => {
      setReferencesViewport(viewport);
    },
    [setReferencesViewport]
  );

  // ---------------------------------------------------------------------------
  // CREATE NOTE BUTTON
  // ---------------------------------------------------------------------------

  const handleCreateNote = useCallback(() => {
    // Check max node count (notes + references combined)
    if (referenceNodes.length + noteNodes.length >= NODE_MAX_COUNT) {
      showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
      return;
    }

    saveHistory();

    // Default note dimensions
    const DEFAULT_NOTE_WIDTH = 300;
    const DEFAULT_NOTE_HEIGHT = 200;

    // Use last clicked position (or viewport center as fallback)
    let targetX = lastClickedPositionRef.current.x;
    let targetY = lastClickedPositionRef.current.y;

    // If no click position recorded (e.g., first use), use viewport center
    if (targetX === 0 && targetY === 0) {
      const viewport = getViewport();
      if (!viewport) return;
      targetX = (-viewport.x + window.innerWidth / 2) / viewport.zoom;
      targetY = (-viewport.y + window.innerHeight / 2) / viewport.zoom;
    }

    // Center the note on the target position
    const centeredPosition = {
      x: targetX - DEFAULT_NOTE_WIDTH / 2,
      y: targetY - DEFAULT_NOTE_HEIGHT / 2,
    };

    const newNote = addNoteNode(centeredPosition);
    selectNoteNode(newNote.id);
    showNotification('New note created', 'info', 1500);
  }, [
    referenceNodes.length,
    noteNodes.length,
    saveHistory,
    addNoteNode,
    selectNoteNode,
    showNotification,
    reactFlowInstance,
  ]);

  // ---------------------------------------------------------------------------
  // HELP TOOLTIP FOR REFERENCE VIEW
  // ---------------------------------------------------------------------------

  // Track if user has dismissed the help tooltip
  const [showHelpTooltip, setShowHelpTooltip] = useState(() => {
    return typeof window !== 'undefined'
      ? localStorage.getItem('emberboard-hideRefHelpTooltip') !== 'true'
      : true;
  });

  // Track if we're hovering over empty space
  const [isHoveringEmpty, setIsHoveringEmpty] = useState(false);

  // Track when we should show the tooltip (after delay)
  const [showTooltipDelayed, setShowTooltipDelayed] = useState(false);

  // Hide tooltip after 8 nodes
  const shouldShowTooltip = showHelpTooltip && (referenceNodes.length + noteNodes.length) < 8;

  // Delay showing tooltip
  useEffect(() => {
    if (shouldShowTooltip && isHoveringEmpty) {
      const timer = setTimeout(() => {
        setShowTooltipDelayed(true);
      }, 1000); // 1 second delay
      return () => clearTimeout(timer);
    } else {
      setShowTooltipDelayed(false);
    }
  }, [shouldShowTooltip, isHoveringEmpty]);

  const tooltipStyles: CSSProperties = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'var(--nb-bg-tertiary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '12px 16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    maxWidth: '280px',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    zIndex: 100,
    opacity: shouldShowTooltip && isHoveringEmpty && showTooltipDelayed ? 0.5 : 0,
    transition: 'opacity 0.5s ease-in-out',
    pointerEvents: 'none',
  };

  const tooltipCloseStyles: CSSProperties = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: 'var(--nb-text-secondary)',
    padding: '0 4px',
    pointerEvents: 'auto',
  };

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--nb-bg-secondary)',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      style={containerStyles}
      onMouseMove={(e) => {
        // Check if mouse is over empty space (not over any node)
        const target = e.target as HTMLElement;
        if (!target.closest('.react-flow__node') && !target.closest('.react-flow__edge')) {
          setIsHoveringEmpty(true);
        } else {
          setIsHoveringEmpty(false);
        }
      }}
      onMouseLeave={() => setIsHoveringEmpty(false)}
      onContextMenu={handleContextMenu}
    >
      <ReactFlow
        key="reference-view" // Force unique instance for this view
        nodes={flowNodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onDoubleClick={onDoubleClickHandler}
        onDrop={handlePaneDrop}
        onDragOver={handlePaneDragOver}
        onDragEnter={handlePaneDragEnter}
        onDragLeave={handlePaneDragLeave}
        onSelectionChange={onSelectionChange}
        onViewportChange={onViewportChange}
        defaultViewport={referencesViewport}
        selectionOnDrag
        panOnDrag
        selectionKeyCode="Shift"
        multiSelectionKeyCode="Shift"
        selectionMode={SelectionMode.Partial}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        minZoom={0.05}
        maxZoom={4}
        style={{ background: 'var(--nb-bg-secondary)' }}
        deleteKeyCode="Delete"
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="var(--nb-border-secondary)"
        />
        <Controls
          showInteractive={false}
          orientation="horizontal"
          style={{
            backgroundColor: 'var(--nb-bg-tertiary)',
            border: '1px solid var(--nb-border-primary)',
            borderRadius: 'var(--nb-radius-sm)',
            left: '10px',
            bottom: '10px',
            transform: 'scale(0.75)',
            transformOrigin: 'bottom left',
          }}
        >
          <ControlButton
            onClick={handleCreateNote}
            title="Create Note (N)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </ControlButton>
        </Controls>
      </ReactFlow>

      {/* Drag overlay - shows when dragging files/images over the canvas */}
      {isDraggingOver && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(228, 112, 25, 0.15)', // Brand orange with low opacity
          border: '3px dashed var(--nb-accent-primary)',
          borderRadius: 'var(--nb-radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none', // Let events pass through to ReactFlow
          zIndex: 50,
        }}>
          <div style={{
            backgroundColor: 'var(--nb-bg-secondary)',
            padding: '24px 48px',
            borderRadius: 'var(--nb-radius-md)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
          }}>
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1.5"
              style={{ 
                margin: '0 auto 16px',
                color: 'var(--nb-accent-primary)',
              }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p style={{
              margin: 0,
              fontSize: 'var(--nb-font-size-lg)',
              fontWeight: 600,
              color: 'var(--nb-text-primary)',
            }}>
              Drop to create reference
            </p>
            <p style={{
              margin: '8px 0 0',
              fontSize: 'var(--nb-font-size-sm)',
              color: 'var(--nb-text-secondary)',
            }}>
              Supports files, web images & URLs
            </p>
          </div>
        </div>
      )}

      {referenceNodes.length === 0 && noteNodes.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'var(--nb-text-muted)',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          <p>Drag images here or paste from clipboard</p>
          <p style={{ fontSize: 'var(--nb-font-size-xs)', marginTop: 'var(--nb-space-xs)' }}>
            Double-click to create an empty reference, or press <kbd style={{
              backgroundColor: 'var(--nb-bg-tertiary)',
              padding: '2px 6px',
              borderRadius: 'var(--nb-radius-xs)',
              border: '1px solid var(--nb-border-primary)',
              fontFamily: 'monospace'
            }}>N</kbd> to create a note
          </p>
        </div>
      )}

      {/* Help tooltip for when nodes exist but user hovers over empty space */}
      {showHelpTooltip && (
        <div style={tooltipStyles}>
          <button
            style={tooltipCloseStyles}
            onClick={() => {
              localStorage.setItem('emberboard-hideRefHelpTooltip', 'true');
              setShowHelpTooltip(false);
            }}
            title="Dismiss help tooltip"
          >
            ×
          </button>
          <p style={{ margin: 0, fontWeight: 600, marginBottom: '8px' }}>Quick Tips:</p>
          <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--nb-font-size-xs)' }}>
            <li style={{ marginBottom: '6px' }}>Double-click to add a reference image</li>
            <li style={{ marginBottom: '6px' }}>
              Press <kbd style={{
                backgroundColor: 'var(--nb-bg-primary)',
                padding: '1px 4px',
                borderRadius: 'var(--nb-radius-xs)',
                border: '1px solid var(--nb-border-primary)',
                fontFamily: 'monospace',
                fontSize: 'var(--nb-font-size-xs)'
              }}>N</kbd> to create a note
            </li>
            <li style={{ marginBottom: '6px' }}>Drag images or paste from clipboard</li>
            <li style={{ marginBottom: '6px' }}>Hold <kbd style={{
              backgroundColor: 'var(--nb-bg-primary)',
              padding: '1px 4px',
              borderRadius: 'var(--nb-radius-xs)',
              border: '1px solid var(--nb-border-primary)',
              fontFamily: 'monospace',
              fontSize: 'var(--nb-font-size-xs)'
            }}>Alt</kbd> + drag to duplicate</li>
            <li>Web images: drag directly from browser</li>
          </ul>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems}
          onClose={handleCloseContextMenu}
        />
      )}
    </div>
  );
}

/**
 * Wrapper component that provides ReactFlowProvider
 */
function ReferenceLibraryView() {
  return (
    <ReactFlowProvider>
      <ReferenceLibraryViewInner />
    </ReactFlowProvider>
  );
}

export default ReferenceLibraryView;
